package com.steamstreet.vegasful.browser.account.subscriptions

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.graphql.api.InteractInput
import com.steamstreet.vegasful.graphql.api.InteractionType
import com.steamstreet.vegasful.web.ClientController
import com.steamstreet.vegasful.web.EntityViewedController
import com.steamstreet.vegasful.web.MORE_INFO_LINK_ATTRIBUTE
import com.steamstreet.vegasful.web.TICKETS_LINK_ATTRIBUTE
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import org.w3c.dom.Element
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.events.MouseEvent

/**
 * How far through the scrollable part of the page the visitor must get before the visit counts as
 * showing additional depth rather than a glance.
 */
private const val DEPTH_SCROLL_FRACTION = 0.5

/**
 * A page with less than this much to scroll is short enough that reaching the bottom says nothing
 * about the visitor's interest.
 */
private const val MINIMUM_SCROLLABLE_PIXELS = 400.0

/**
 * How long a click waits for its interaction to reach the server before navigating away. The
 * request is lost if the page unloads first, so a brief wait buys a far more reliable signal.
 */
private const val NAVIGATION_RECORD_TIMEOUT_MS = 500L

/**
 * Links to these entity types represent following a thread of interest out of the current page.
 * Events are excluded: moving from a listing to an event is the ordinary path through the site and
 * says nothing extra about the page being left.
 */
private val RELATED_ENTITY_PREFIXES = listOf("/performers/", "/venues/", "/tags/")

/**
 * Records engagement with the entity whose page is being shown.
 *
 * The visit itself is sent as a VIEWED interaction on load. A MORE_INFO interaction follows if the
 * visitor shows additional depth — scrolling well into the page, following a link to a related
 * performer, venue or tag, or opening the entity's own site. Ticket links are recorded as TICKETS.
 */
class VisitController(val element: Element, private val context: CoroutineScope) : ClientController {
    private val entityPath = element.getAttribute(EntityViewedController.pathAttribute)

    /**
     * Depth is only worth reporting once per page view. The server collapses repeats within a day
     * anyway, so this is about avoiding pointless calls rather than about correctness.
     */
    private var depthRecorded = false

    override suspend fun run() {
        // do nothing if the user isn't logged in.
        if (!Account.isLoggedIn()) return
        val path = entityPath ?: return

        record(InteractionType.VIEWED, path)
        listenForScrollDepth(path)
        listenForRelatedEntityLinks(path)
        listenForMarkedLinks()
    }

    private suspend fun record(type: InteractionType, path: String) {
        GraphQL.mutation {
            subscriptions {
                this.interact(InteractInput(type, path))
            }
        }
    }

    /**
     * Report that the visitor went deeper into this entity, if that hasn't been reported already.
     */
    private fun recordDepth(path: String) {
        if (depthRecorded) return
        depthRecorded = true
        context.launch {
            record(InteractionType.MORE_INFO, path)
        }
    }

    private fun listenForScrollDepth(path: String) {
        window.addEventListener("scroll", {
            val scrollable = (document.documentElement?.scrollHeight?.toDouble() ?: 0.0) - window.innerHeight
            if (scrollable >= MINIMUM_SCROLLABLE_PIXELS && window.scrollY >= scrollable * DEPTH_SCROLL_FRACTION) {
                recordDepth(path)
            }
        })
    }

    /**
     * Watch for the visitor following a link to a related entity. This listens on pointer-down so
     * that the request is under way before the browser starts tearing the page down.
     */
    private fun listenForRelatedEntityLinks(path: String) {
        document.addEventListener("pointerdown", { event ->
            val anchor = (event.target as? Element)?.closest("a[href]") as? HTMLAnchorElement
            if (anchor != null &&
                // Site navigation isn't a signal of interest in the entity being viewed.
                anchor.closest("nav, header, footer") == null &&
                RELATED_ENTITY_PREFIXES.any { anchor.pathname.startsWith(it) }
            ) {
                recordDepth(path)
            }
        })
    }

    /**
     * Watch for clicks on links the page has marked as leading to tickets or to further detail.
     */
    private fun listenForMarkedLinks() {
        document.addEventListener("click", { event ->
            val mouse = event as? MouseEvent ?: return@addEventListener
            val target = event.target as? Element ?: return@addEventListener

            val (type, markedPath) = markedInteraction(target) ?: return@addEventListener
            val anchor = target.closest("a[href]") as? HTMLAnchorElement
            val href = anchor?.href

            // A modified click, or a link that opens in its own tab, leaves this page intact, so
            // the interaction can be sent without holding anything up.
            val leavesPageIntact = href == null || anchor.target == "_blank" ||
                    mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey ||
                    mouse.button.toInt() != 0

            if (leavesPageIntact) {
                context.launch {
                    record(type, markedPath)
                }
            } else {
                // Otherwise this page is about to be unloaded, which would cancel the request.
                // Send it first, then follow the link regardless of how that turned out.
                event.preventDefault()
                context.launch {
                    withTimeoutOrNull(NAVIGATION_RECORD_TIMEOUT_MS) {
                        record(type, markedPath)
                    }
                    window.location.href = href
                }
            }
        })
    }

    /**
     * Find the interaction, if any, that the page has attached to the region around this element.
     */
    private fun markedInteraction(target: Element): Pair<InteractionType, String>? {
        target.closest("[$TICKETS_LINK_ATTRIBUTE]")?.getAttribute(TICKETS_LINK_ATTRIBUTE)?.let {
            return InteractionType.TICKETS to it
        }
        target.closest("[$MORE_INFO_LINK_ATTRIBUTE]")?.getAttribute(MORE_INFO_LINK_ATTRIBUTE)?.let {
            return InteractionType.MORE_INFO to it
        }
        return null
    }
}
